import { useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { sumLines, useCreateInvoice, useSearchSuppliers } from '../../api/outbound.js';
import type { InvoiceLine, Supplier } from '../../api/types.js';
import { formatAmount } from '../invoices/format.js';
import { AttachmentsStep, type CommittedAttachment } from './AttachmentsStep.js';

type Step = 0 | 1 | 2 | 3 | 4;
const STEPS: { id: Step; label: string }[] = [
  { id: 0, label: 'Destinataire' },
  { id: 1, label: 'Métadonnées' },
  { id: 2, label: 'Lignes' },
  { id: 3, label: 'Pièces jointes' },
  { id: 4, label: 'Revue' },
];

interface Draft {
  recipient: { siren: string | null; name: string };
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  lines: InvoiceLine[];
  attachments: CommittedAttachment[];
}

function emptyDraft(): Draft {
  const today = new Date();
  const due = new Date(today);
  due.setDate(due.getDate() + 60);
  return {
    recipient: { siren: '', name: '' },
    invoiceNumber: '',
    invoiceDate: today.toISOString().slice(0, 10),
    dueDate: due.toISOString().slice(0, 10),
    currency: 'EUR',
    lines: [{ description: '', quantity: 1, unitPrice: 0, vatRate: 0.2 }],
    attachments: [],
  };
}

interface Props {
  tenantId: string;
  tenantName: string;
}

export function NewInvoiceWizard({ tenantId, tenantName }: Props) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [step, setStep] = useState<Step>(0);
  const [inflightUploads, setInflightUploads] = useState(0);
  const createMutation = useCreateInvoice();
  const [submitError, setSubmitError] = useState<string | null>(null);

  function update<K extends keyof Draft>(k: K, v: Draft[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  const isStepValid = ((): boolean => {
    if (step === 0) return draft.recipient.name.trim().length > 0;
    if (step === 1) {
      return (
        draft.invoiceNumber.trim().length > 0 &&
        draft.invoiceNumber.trim().length <= 50 &&
        Boolean(draft.invoiceDate) &&
        Boolean(draft.dueDate) &&
        new Date(draft.dueDate).getTime() >= new Date(draft.invoiceDate).getTime() &&
        /^[A-Z]{3}$/.test(draft.currency)
      );
    }
    if (step === 2) {
      return (
        draft.lines.length > 0 &&
        draft.lines.every(
          (l) =>
            l.description.trim().length > 0 &&
            l.quantity > 0 &&
            l.unitPrice >= 0 &&
            l.vatRate >= 0 &&
            l.vatRate <= 0.3,
        )
      );
    }
    if (step === 3) {
      // Attachments optional; block Suivant while uploads in flight.
      return inflightUploads === 0;
    }
    return true;
  })();

  async function submit() {
    setSubmitError(null);
    const sanitizedSiren = draft.recipient.siren?.trim() ?? '';
    const total = sumLines(draft.lines);
    const payload = {
      tenantId,
      recipient: {
        siren: sanitizedSiren.length === 9 ? sanitizedSiren : null,
        name: draft.recipient.name.trim(),
      },
      invoiceNumber: draft.invoiceNumber.trim(),
      invoiceDate: draft.invoiceDate,
      dueDate: draft.dueDate,
      currency: draft.currency,
      amountTotal: total,
      lines: draft.lines.map((l) => ({
        description: l.description.trim(),
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        vatRate: l.vatRate,
      })),
      ...(draft.attachments.length > 0
        ? {
            attachments: draft.attachments.map((a) => ({
              objectName: a.objectName,
              contentType: a.contentType,
              bytes: a.bytes,
            })),
          }
        : {}),
    };
    try {
      const created = await createMutation.mutateAsync(payload);
      navigate(`/t/${tenantId}/invoices/${encodeURIComponent(created.primaryKey)}`);
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 409) setSubmitError('Ce numéro de facture existe déjà pour cette société.');
      else if (status === 400)
        setSubmitError('Validation refusée. Vérifiez les montants et lignes.');
      else setSubmitError('Échec de la création de la facture.');
    }
  }

  return (
    <section className="invoices" aria-label="Créer une facture">
      <header className="invoices__head">
        <div>
          <div className="eyebrow">{tenantName}</div>
          <h1 className="display invoices__title">Créer une facture</h1>
        </div>
        <Link to={`/t/${tenantId}/invoices`} className="btn-ghost">
          Annuler
        </Link>
      </header>

      <ol className="wizard-stepper" aria-label="Étapes" data-testid="wizard-stepper">
        {STEPS.map((s) => (
          <li
            key={s.id}
            className={`wizard-step${s.id === step ? ' is-active' : ''}${s.id < step ? ' is-done' : ''}`}
            data-testid={`wizard-step-${s.id}`}
          >
            <span className="wizard-step__index">{s.id + 1}</span>
            <span className="wizard-step__label">{s.label}</span>
          </li>
        ))}
      </ol>

      <div className="wizard-body">
        {step === 0 ? (
          <RecipientStep
            tenantId={tenantId}
            value={draft.recipient}
            onChange={(v) => update('recipient', v)}
          />
        ) : null}
        {step === 1 ? (
          <MetadataStep draft={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
        ) : null}
        {step === 2 ? (
          <LinesStep lines={draft.lines} onChange={(lines) => update('lines', lines)} />
        ) : null}
        {step === 3 ? (
          <AttachmentsStep
            tenantId={tenantId}
            attachments={draft.attachments}
            onChange={(next) => update('attachments', next)}
            onInflightChange={setInflightUploads}
          />
        ) : null}
        {step === 4 ? <ReviewStep draft={draft} /> : null}
      </div>

      {submitError ? (
        <div className="invoices__error" role="alert" data-testid="wizard-submit-error">
          <span>{submitError}</span>
        </div>
      ) : null}

      <nav className="wizard-foot" aria-label="Navigation du formulaire">
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setStep((s) => (s > 0 ? ((s - 1) as Step) : s))}
          disabled={step === 0 || createMutation.isPending}
          data-testid="wizard-prev"
        >
          ← Précédent
        </button>
        {step < 4 ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setStep((s) => (s + 1) as Step)}
            disabled={!isStepValid}
            data-testid="wizard-next"
          >
            Suivant →
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={createMutation.isPending}
            data-testid="wizard-submit"
          >
            {createMutation.isPending ? 'Envoi…' : 'Soumettre la facture'}
          </button>
        )}
      </nav>
    </section>
  );
}

/* ---------- Step components ---------- */

function RecipientStep({
  tenantId,
  value,
  onChange,
}: {
  tenantId: string;
  value: { siren: string | null; name: string };
  onChange: (v: { siren: string | null; name: string }) => void;
}) {
  const [query, setQuery] = useState(value.name);
  const suppliers = useSearchSuppliers(tenantId, query);

  function pick(s: Supplier) {
    onChange({ siren: s.siren ?? null, name: s.name ?? '' });
    setQuery(s.name ?? '');
  }

  return (
    <article className="invoice-detail__card wizard-card">
      <label className="dispute-form__label">
        <span className="eyebrow">Destinataire — nom ou SIREN</span>
        <input
          type="search"
          className="modal__search"
          placeholder="Recherchez un fournisseur connu ou saisissez un nouveau nom"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange({ ...value, name: e.target.value });
          }}
          data-testid="wizard-recipient-search"
        />
      </label>
      {suppliers.data && suppliers.data.length > 0 ? (
        <ul className="assign-members" data-testid="wizard-recipient-suggestions">
          {suppliers.data.map((s) => (
            <li key={`${s.siren ?? ''}-${s.name ?? ''}`} className="assign-member">
              <button
                type="button"
                className="assign-member__btn"
                onClick={() => pick(s)}
                data-testid={`wizard-supplier-${s.siren ?? 'unknown'}`}
              >
                <span className="assign-member__avatar">
                  {(s.name ?? '?').slice(0, 2).toUpperCase()}
                </span>
                <span className="assign-member__main">
                  <span className="assign-member__name">{s.name ?? '—'}</span>
                  <span className="assign-member__email muted">
                    {s.siren ? `SIREN ${s.siren}` : 'SIREN inconnu'}
                  </span>
                </span>
                <span className="assign-member__role muted">{s.invoiceCount} factures</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="dispute-form__label">
        <span className="eyebrow">SIREN (9 chiffres, optionnel pour non-FR)</span>
        <input
          type="text"
          className="modal__search"
          placeholder="ex. 552100891"
          value={value.siren ?? ''}
          maxLength={9}
          onChange={(e) => onChange({ ...value, siren: e.target.value })}
          data-testid="wizard-recipient-siren"
        />
      </label>
    </article>
  );
}

function MetadataStep({
  draft,
  onChange,
}: {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
}) {
  return (
    <article className="invoice-detail__card wizard-card">
      <div className="wizard-grid">
        <label className="dispute-form__label">
          <span className="eyebrow">Numéro de facture *</span>
          <input
            type="text"
            className="modal__search"
            maxLength={50}
            value={draft.invoiceNumber}
            onChange={(e) => onChange({ invoiceNumber: e.target.value })}
            data-testid="wizard-invoice-number"
          />
        </label>
        <label className="dispute-form__label">
          <span className="eyebrow">Devise *</span>
          <input
            type="text"
            className="modal__search"
            maxLength={3}
            value={draft.currency}
            onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })}
            data-testid="wizard-currency"
          />
        </label>
        <label className="dispute-form__label">
          <span className="eyebrow">Date d'émission *</span>
          <input
            type="date"
            className="modal__search"
            value={draft.invoiceDate}
            onChange={(e) => onChange({ invoiceDate: e.target.value })}
            data-testid="wizard-invoice-date"
          />
        </label>
        <label className="dispute-form__label">
          <span className="eyebrow">Date d'échéance *</span>
          <input
            type="date"
            className="modal__search"
            value={draft.dueDate}
            onChange={(e) => onChange({ dueDate: e.target.value })}
            data-testid="wizard-due-date"
          />
        </label>
      </div>
    </article>
  );
}

function LinesStep({
  lines,
  onChange,
}: {
  lines: InvoiceLine[];
  onChange: (next: InvoiceLine[]) => void;
}) {
  function patch(idx: number, p: Partial<InvoiceLine>) {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...p } : l)));
  }
  function addRow() {
    onChange([...lines, { description: '', quantity: 1, unitPrice: 0, vatRate: 0.2 }]);
  }
  function removeRow(idx: number) {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== idx));
  }
  const total = sumLines(lines);

  return (
    <article className="invoice-detail__card wizard-card">
      <table className="wizard-lines">
        <thead>
          <tr>
            <th>Désignation</th>
            <th>Quantité</th>
            <th>PU HT</th>
            <th>TVA</th>
            <th>Total TTC</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} data-testid={`wizard-line-${i}`}>
              <td>
                <input
                  type="text"
                  className="modal__search"
                  value={l.description}
                  onChange={(e) => patch(i, { description: e.target.value })}
                  data-testid={`wizard-line-desc-${i}`}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="modal__search wizard-lines__num"
                  value={l.quantity}
                  onChange={(e) => patch(i, { quantity: Number(e.target.value) })}
                  data-testid={`wizard-line-qty-${i}`}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="modal__search wizard-lines__num"
                  value={l.unitPrice}
                  onChange={(e) => patch(i, { unitPrice: Number(e.target.value) })}
                  data-testid={`wizard-line-price-${i}`}
                />
              </td>
              <td>
                <select
                  className="modal__search wizard-lines__num"
                  value={String(l.vatRate)}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    patch(i, { vatRate: Number(e.target.value) })
                  }
                  data-testid={`wizard-line-vat-${i}`}
                >
                  <option value="0">0%</option>
                  <option value="0.055">5,5%</option>
                  <option value="0.1">10%</option>
                  <option value="0.2">20%</option>
                </select>
              </td>
              <td className="invoice-row__amount">
                {formatAmount(l.quantity * l.unitPrice * (1 + l.vatRate), 'EUR')}
              </td>
              <td>
                <button
                  type="button"
                  className="comment__delete"
                  onClick={() => removeRow(i)}
                  disabled={lines.length <= 1}
                  aria-label="Supprimer la ligne"
                  data-testid={`wizard-line-remove-${i}`}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="wizard-lines__foot">
        <button type="button" className="btn-ghost" onClick={addRow} data-testid="wizard-line-add">
          + Ajouter une ligne
        </button>
        <div className="invoice-detail__amount" data-testid="wizard-total">
          Total : {formatAmount(total, 'EUR')}
        </div>
      </div>
    </article>
  );
}

function ReviewStep({ draft }: { draft: Draft }) {
  const total = sumLines(draft.lines);
  return (
    <article className="invoice-detail__card wizard-card">
      <div className="invoice-detail__parties">
        <div className="invoice-detail__card invoice-detail__party">
          <div className="eyebrow">Destinataire</div>
          <div className="invoice-detail__party-name">{draft.recipient.name || '—'}</div>
          <div className="muted">
            {draft.recipient.siren ? `SIREN ${draft.recipient.siren}` : 'SIREN non renseigné'}
          </div>
        </div>
        <div className="invoice-detail__card invoice-detail__party">
          <div className="eyebrow">Document</div>
          <div className="invoice-detail__party-name">{draft.invoiceNumber || '—'}</div>
          <div className="muted">
            Émise le {draft.invoiceDate} · Échéance {draft.dueDate}
          </div>
        </div>
      </div>
      <table className="wizard-lines">
        <thead>
          <tr>
            <th>Désignation</th>
            <th>Qté</th>
            <th>PU HT</th>
            <th>TVA</th>
            <th>Total TTC</th>
          </tr>
        </thead>
        <tbody>
          {draft.lines.map((l, i) => (
            <tr key={i}>
              <td>{l.description || '—'}</td>
              <td>{l.quantity}</td>
              <td>{formatAmount(l.unitPrice, draft.currency)}</td>
              <td>{(l.vatRate * 100).toFixed(1)}%</td>
              <td>{formatAmount(l.quantity * l.unitPrice * (1 + l.vatRate), draft.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="wizard-lines__foot">
        <span className="muted">
          {draft.lines.length} ligne{draft.lines.length > 1 ? 's' : ''}
        </span>
        <div className="invoice-detail__amount" data-testid="wizard-review-total">
          Total à facturer : {formatAmount(total, draft.currency)}
        </div>
      </div>
      <div data-testid="wizard-review-attachments">
        <div className="eyebrow">
          Pièces jointes ({draft.attachments.length})
        </div>
        {draft.attachments.length === 0 ? (
          <div className="muted">Aucune pièce jointe.</div>
        ) : (
          <ul className="attachments-list">
            {draft.attachments.map((a, i) => (
              <li
                key={a.objectName}
                className="attachments-list__item"
                data-testid={`wizard-review-attachment-${i}`}
              >
                <div className="attachments-list__name">{a.filename}</div>
                <div className="muted attachments-list__meta">
                  {(a.bytes / (1024 * 1024)).toFixed(2)} Mo
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
